import { useCallback, useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import type { PublicUser } from "../../shared/contracts";
import { api } from "../lib/api";
import { initials } from "../lib/format";
import { StatePanel } from "./StatePanel";

export type ProfileConnectionKind = "following" | "followers";

interface ProfileConnectionsDialogProps {
  kind: ProfileConnectionKind | null;
  profileId: string;
  profileNickname: string;
  onClose(): void;
}

function roleLabel(role: PublicUser["role"]): string {
  if (role === "seller") return "号商";
  if (role === "admin") return "管理员";
  return "玩家";
}

export function ProfileConnectionsDialog({ kind, profileId, profileNickname, onClose }: ProfileConnectionsDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [items, setItems] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!kind) return;
    setLoading(true);
    setError(null);
    setItems([]);
    try {
      const result = await api<{ items: PublicUser[] }>(`/users/${profileId}/${kind}`);
      setItems(result.items);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "用户列表加载失败");
    } finally {
      setLoading(false);
    }
  }, [kind, profileId]);

  useEffect(() => { void load(); }, [load]);

  const title = kind === "following" ? "关注" : "粉丝";
  const description = kind === "following"
    ? `${profileNickname}正在关注的用户`
    : `正在关注${profileNickname}的用户`;

  return (
    <Dialog
      className="profile-connections-dialog"
      open={kind !== null}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby="profile-connections-title"
      aria-describedby="profile-connections-description"
    >
      <DialogContent className="profile-connections-dialog-content">
        <header className="profile-connections-heading">
          <div>
            <DialogTitle id="profile-connections-title">{title}</DialogTitle>
            <DialogContentText id="profile-connections-description">{description}</DialogContentText>
          </div>
          <Tooltip title="关闭">
            <IconButton type="button" aria-label={`关闭${title}列表`} onClick={onClose}><X aria-hidden="true" /></IconButton>
          </Tooltip>
        </header>
        {loading ? <div className="profile-connections-state"><StatePanel kind="loading" title={`正在加载${title}列表`} /></div> : null}
        {!loading && error ? <div className="profile-connections-state"><StatePanel kind="error" title={`${title}列表加载失败`} description={error} onRetry={() => void load()} /></div> : null}
        {!loading && !error && items.length === 0 ? <div className="profile-connections-state"><StatePanel kind="empty" title={`还没有${title}`} description={kind === "following" ? "关注用户后会显示在这里。" : "有人关注后会显示在这里。"} /></div> : null}
        {!loading && !error && items.length > 0 ? <List className="profile-connections-list" aria-label={`${profileNickname}的${title}列表`}>
          {items.map((item) => <ListItemButton className="profile-connection-item" component={Link} to={`/seller/${item.id}`} key={item.id} onClick={onClose}>
            <ListItemAvatar><Avatar src={item.avatarUrl ?? undefined}>{initials(item.nickname)}</Avatar></ListItemAvatar>
            <ListItemText primary={item.nickname} secondary={`${roleLabel(item.role)}${item.bio ? ` · ${item.bio}` : ""}`} slotProps={{ secondary: { component: "span" } }} />
          </ListItemButton>)}
        </List> : null}
      </DialogContent>
    </Dialog>
  );
}
